import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Flag } from '../../../icons/mono/Flag';
import { DropdownMenuController } from '../../DropdownMenu';
import { SuccessDialog } from '../internals/SuccessDialog';
import { type FeedbackType, ReportDialog } from './ReportDialog/ReportDialog';

interface ReportFormData {
  reportText: string;
  reportType: string;
}

interface ReviewOptionType {
  id: string;
  title: string;
  description: string;
}

interface VotingDropdownMenuControllerProps {
  options: ReviewOptionType[];
  onSubmit: (data: ReportFormData) => Promise<void>;
  isSuccess?: boolean;
  onSuccessHandled?: () => void;
  feedbackType: FeedbackType;
  reviewId?: number;
  isReporting?: boolean;
}

const VotingDropdownMenuController = ({
  options,
  onSubmit,
  isSuccess,
  onSuccessHandled,
  feedbackType,
  isReporting,
  children,
}: PropsWithChildren<VotingDropdownMenuControllerProps>) => {
  const [ reportModalOpen, setReportModalOpen ] = useState(false);
  const [ selectedReportType, setSelectedReportType ] = useState('');
  const [ successDialogOpen, setSuccessDialogOpen ] = useState(false);

  const handleReportDialogOnOpenChange = () => {
    setReportModalOpen((prev) => !prev);
  };

  const handleSuccessDialogClosed = () => {
    setSuccessDialogOpen(false);
  };

  useEffect(() => {
    if (isSuccess) {
      setReportModalOpen(false);
      setSuccessDialogOpen(true);
      onSuccessHandled?.();
    }
  }, [isSuccess, onSuccessHandled]);

  const menuOptions = useMemo(() => {
    return options.map(({ id, title }) => ({
      id,
      label: title,
      icon: <Flag />,
      onClick: () => {
        setSelectedReportType(id);
        setReportModalOpen(true);
      },
    }));
  }, [options]);

  const menu = {
    title: 'Report Reason',
    options: menuOptions,
  };

  return (
    <>
      {!reportModalOpen && (
        <DropdownMenuController menu={menu}>
          {/*TODO: add no reportTypes found state*/}
          {children}
        </DropdownMenuController>
      )}
      <ReportDialog
        key={selectedReportType}
        isOpen={reportModalOpen}
        reviewReportTypes={options}
        selectedReportType={selectedReportType}
        onOpenChange={handleReportDialogOnOpenChange}
        submitReview={onSubmit}
        mode={feedbackType}
        isReporting={isReporting}
      />

      <SuccessDialog
        isOpen={successDialogOpen}
        onClose={handleSuccessDialogClosed}
      />
    </>
  );
};

export {
  VotingDropdownMenuController,
  type VotingDropdownMenuControllerProps,
  type ReviewOptionType,
  type ReportFormData,
};
